import { queryKeys } from 'core/api/queryKeys';

export function invalidateLocalizedContent(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: queryKeys.assessment }),
    queryClient.invalidateQueries({ queryKey: ['assessment'] }),
    queryClient.invalidateQueries({ queryKey: ['results'] }),
    queryClient.invalidateQueries({ queryKey: ['consultations'] }),
    queryClient.invalidateQueries({ queryKey: ['support'] }),
    queryClient.invalidateQueries({ queryKey: ['legal'] }),
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  ]);
}

export function invalidateAfterProfileOrLocale(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.me }),
    queryClient.invalidateQueries({ queryKey: ['auth', 'profile-options'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: queryKeys.legalCurrent }),
    invalidateLocalizedContent(queryClient),
  ]);
}

export function invalidateAfterLegalAcceptance(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.me }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: queryKeys.legalCurrent }),
    queryClient.invalidateQueries({ queryKey: ['legal'] }),
  ]);
}

export function invalidateAfterAssessmentStart(queryClient, attemptId) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: queryKeys.assessment }),
    queryClient.invalidateQueries({ queryKey: queryKeys.attempt(attemptId) }),
  ]);
}

export function invalidateAfterAnswerSave(queryClient, attemptId) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.attempt(attemptId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.assessment }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
  ]);
}

export function invalidateAfterAssessmentSubmit(queryClient, attemptId) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.attempt(attemptId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: ['results'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.result(attemptId) }),
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount }),
  ]);
}

export function invalidateAfterConsultationMutation(queryClient, consultationId) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['consultations'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.consultation(consultationId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: ['results'] }),
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount }),
  ]);
}

export function invalidateAfterSupportCreate(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.supportCategories }),
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount }),
  ]);
}

export function invalidateAfterNotifications(queryClient, notification) {
  const actionType = notification?.action_type || notification?.actionType;
  const consultationId = notification?.action?.consultation_id || notification?.action?.consultationId;
  const refreshConsultation = actionType === 'open_consultation' || Boolean(consultationId);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount }),
    queryClient.invalidateQueries({ queryKey: queryKeys.home }),
    queryClient.invalidateQueries({ queryKey: ['consultations'] }),
    refreshConsultation
      ? queryClient.invalidateQueries({ queryKey: queryKeys.consultation(consultationId) })
      : Promise.resolve(),
  ]);
}

export function invalidateAfterCompanyMutation(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.companyBootstrap }),
    queryClient.invalidateQueries({ queryKey: ['company'] }),
  ]);
}

export function invalidateAfterCompanyPurchase(queryClient, purchaseId) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.companyBootstrap }),
    queryClient.invalidateQueries({ queryKey: ['company', 'purchases'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyPurchase(purchaseId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyCredits }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyEntitlements }),
  ]);
}

export function invalidateAfterCodeMutation(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.companyBootstrap }),
    queryClient.invalidateQueries({ queryKey: ['company', 'codes'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyCredits }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyEntitlements }),
  ]);
}

export function invalidateAfterParticipantMutation(queryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['company', 'participants'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.companyBootstrap }),
  ]);
}
